import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { directoryGridOrder } from 'src/app/helper/sortingHelper';
import { GridSettings } from 'src/app/samwebDescription/samweb/dashboard/grid-column-settings';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { DirectoryOrganisationUnitColumnType } from 'src/app/samwebDescription/samweb/directory/directory-column-type';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { BrickGridsState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-grids-state/brick-grids.state';
import {
   DirectoryBrowserAddEditedEntry,
   DirectoryBrowserDeleteEditedEntry,
   DirectoryBrowserEditedSetConfirmationInProgress,
   DirectoryBrowserRemoveEditEntry,
   DirectoryBrowserUpdateEditEntry,
   DirectoryBrowserUpdateEditedEntry,
   DirectoryBrowserUpdateOriginEntry,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.actions';
import {
   DirectoryEditingBehaviourState,
   DirectoryEntryEditInfo,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.state';
import { DirectoryEditRestrictionsState } from 'src/app/store/dashboard/directory-edit-restrictions/directory-edit-restrictions.state';
import { DirectoryStructureSelectors } from 'src/app/store/samwin/directory/directory-structure/directory-structure.selectors';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SessionState } from 'src/app/store/session/session.state';
import { RemoveWindow } from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { WindowType } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { DirectoryEditRestrictionsEntryRemoteDto } from 'src/generated/signalR/directory-edit-restrictions-entry-remote-dto';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-directory-editing-content',
   templateUrl: './directory-editing-content.component.html',
   styleUrls: ['./directory-editing-content.component.scss'],
})
export class DirectoryEditingContentComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();

   @ViewChild('deleteButton', { read: ElementRef })
   public delButton: ElementRef;

   @ViewChild('cancelButton', { read: ElementRef })
   public cancButton: ElementRef;

   @ViewChild('deleteTooltipDirective')
   public deleteTooltipDirective: TooltipDirective;

   @ViewChild('closeTooltipDirective')
   public closeTooltipDirective: TooltipDirective;

   @Input()
   public id: number;

   @Input()
   public areEditableEntriesChanged: boolean;

   @Input()
   public directoryEditInfo$: Observable<DirectoryEntryEditInfo>;

   public editableColumns$: Observable<DirectoryColumn[]>;

   public type: WindowType = 'directory';

   constructor(private store: Store) {}

   public isEditEntry = false;

   public config$: Observable<GridSettings>;

   public areEditableEntriesEqual$: Observable<boolean>;

   public areEditableEntriesEqual: boolean;
   public compareEntry: DirectoryEntryRemoteDto;

   @Select(SessionState.getAuthenticatedUserId)
   public authenticatedUserID$: Observable<number>;

   public directoryRestrictions$: Observable<DirectoryEditRestrictionsEntryRemoteDto[]>;

   @Select(DirectoryStructureState.selectedDirectoryDirectoryColumn)
   public selectedDirectoryDirectoryColumn$: Observable<DirectoryColumn>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))
   public isAttendant$: Observable<boolean>;

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit(): void {
      this.isEditEntry = this.id > 0;

      this.config$ = this.directoryEditInfo$
         .pipe(map((info) => info?.sourceBrickInstanceId))
         .pipe(
            switchMap((brickInstanceId) =>
               this.store.select(BrickGridsState.getBrickColumnConfigurationForDirectoryBrick).pipe(map((getEntry) => getEntry(brickInstanceId, 'default')))
            )
         );

      const ordered$ = combineLatest([this.config$, this.store.select(DirectoryStructureState.editableColumns)]).pipe(
         map(([setting, editableColumns]) => {
            return directoryGridOrder(editableColumns, setting).filter((dir) => dir.organisationUnitNumber === '00');
         })
      );

      const originEntry$ = this.directoryEditInfo$.pipe(map((info) => info?.originalEntry));
      const directoryId$ = this.selectedDirectoryDirectoryColumn$.pipe(
         switchMap((directoryColumn) =>
            combineLatest([this.store.select(DirectoryStructureSelectors.getColumnInstance), originEntry$])
               .pipe(switchMap(([getColumnInstance, directoryentry]) => of(getColumnInstance(directoryentry, directoryColumn))))
               .pipe(
                  map((entry) => {
                     if (entry?.isValueSet && entry.value > 0) {
                        return entry.value;
                     }
                     return 0;
                  })
               )
         )
      );
      this.directoryRestrictions$ = this.store.select(DirectoryEditRestrictionsState.getDirectoryEditRestrictionsByGroupIds).pipe(
         switchMap((getRestrictions) =>
            directoryId$.pipe(
               switchMap((directoryId) =>
                  this.authenticatedUserID$.pipe(
                     switchMap((userId) =>
                        this.store.select(SamwinAgentListState.getAgentDetailsForUserId).pipe(
                           map((getAgent) => getAgent(userId)),
                           map((agent) => getRestrictions(agent.memberOfGroups, Number(directoryId)))
                        )
                     )
                  )
               )
            )
         )
      );

      this.editableColumns$ = ordered$;
      this.areEditableEntriesEqual$ = this.store
         .select(DirectoryEditingBehaviourState.areEditableEntriesEqual)
         .pipe(map((areEntriesEqual) => areEntriesEqual(this.id)));

      this.areEditableEntriesEqual$
         .pipe(
            switchMap((areEditableEntriesEqual) =>
               this.directoryEditInfo$.pipe(
                  tap((info) => {
                     this.areEditableEntriesEqual = areEditableEntriesEqual;
                     this.compareEntry = info?.updatedEntry;
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   valueChanged(value: string | number | boolean | Date, directoryColumn: DirectoryColumn) {
      const dirKey = DirectoryOrganisationUnitColumnType.valueByColumnKey(directoryColumn.organisationUnitColumnKey).directoryMapValue;
      this.store.dispatch(new DirectoryBrowserUpdateEditEntry(this.id, dirKey, value));
   }

   updateUnchangedValues(value: string | number | boolean | Date, directoryColumn: DirectoryColumn) {
      const dirKey = DirectoryOrganisationUnitColumnType.valueByColumnKey(directoryColumn.organisationUnitColumnKey).directoryMapValue;
      this.store.dispatch(new DirectoryBrowserUpdateEditEntry(this.id, dirKey, value));
      this.store.dispatch(new DirectoryBrowserUpdateOriginEntry(this.id, dirKey, value));
   }

   takeOther(value: string | number | boolean | Date, directoryColumn: DirectoryColumn) {
      const dirKey = DirectoryOrganisationUnitColumnType.valueByColumnKey(directoryColumn.organisationUnitColumnKey).directoryMapValue;
      this.store.dispatch(new DirectoryBrowserUpdateEditEntry(this.id, dirKey, value));
   }

   onDelete() {
      this.deleteTooltipDirective.show(this.delButton);
      return;
   }

   onDeleteConfirm() {
      if (this.id > 0) {
         this.store.dispatch(new DirectoryBrowserDeleteEditedEntry(this.id));
      }
   }

   onDeleteCancel() {
      this.deleteTooltipDirective.hide();
   }

   onSave() {
      const isExistingItem = this.id > 0;
      if (isExistingItem) {
         // Updating
         if (_.isNil(this.compareEntry)) {
            // No remote changes made, continue
            this.store.dispatch(new DirectoryBrowserUpdateEditedEntry(this.id));
            return;
         }
         if (this.areEditableEntriesEqual) {
            // Remote and local changes match, continue
            this.store.dispatch(new DirectoryBrowserUpdateEditedEntry(this.id));
         } else {
            // Editing conflict, user confirmation required
            this.store.dispatch(new DirectoryBrowserEditedSetConfirmationInProgress(this.id, true));
         }
      } else {
         // Adding
         this.store.dispatch(new DirectoryBrowserAddEditedEntry(this.id));
      }
   }

   closeWindow() {
      this.store.dispatch(new DirectoryBrowserRemoveEditEntry(this.id));
      this.store.dispatch(new RemoveWindow(this.type, this.id.toString()));
   }

   onCancel() {
      if (this.areEditableEntriesChanged) {
         this.closeTooltipDirective.show(this.cancButton);
         return;
      }
      this.closeWindow();
   }

   onCloseConfirm() {
      this.closeWindow();
   }

   onCloseCancel() {
      this.closeTooltipDirective.hide();
   }
}
